import type { ImageNode, TextNode } from '@repo/api-contracts';
import { apply, invert, nodeMatrix, type PointMm } from '@repo/design-renderer';

export type HandleKind =
  | 'move'
  | 'rotate'
  | 'corner-tl'
  | 'corner-tr'
  | 'corner-br'
  | 'corner-bl'
  | 'side-l'
  | 'side-r';
export type BoxNode = ImageNode | TextNode;

export interface HandlePoint {
  readonly kind: HandleKind;
  readonly xMm: number;
  readonly yMm: number;
}

export const ROTATE_HANDLE_OFFSET_PX = 24;
export const HANDLE_HIT_TOLERANCE_PX = 10;
export const MIN_NODE_MM = 1;

/**
 * 화면 축척(CSS px/mm) — 숫자 하나면 두 축이 같고, 사진 위 편집처럼 축마다 다르면 { x, y } 로 준다.
 * 핸들 자리(회전 핸들 24px)·히트 허용 거리(10px)를 화면 px 로 재기 위해서만 쓴다.
 */
export type HandleScale = number | { readonly x: number; readonly y: number };

const axesOf = (scale: HandleScale): { x: number; y: number } =>
  typeof scale === 'number' ? { x: scale, y: scale } : { x: scale.x, y: scale.y };

const center = (node: BoxNode): PointMm => ({
  xMm: node.x + node.width / 2,
  yMm: node.y + node.height / 2,
});

/** 핸들 판정은 플립을 무시한다. */
const withoutFlip = (node: BoxNode): BoxNode =>
  node.type === 'image' ? { ...node, flipX: false, flipY: false } : node;

/** 노드 로컬 점(상자 좌표, mm) → 컴포넌트 mm. */
const localToComponent = (node: BoxNode, x: number, y: number): PointMm => {
  const p = apply(nodeMatrix(node), { x, y });
  return { xMm: p.x, yMm: p.y };
};

/** 컴포넌트 mm → 노드 로컬(상자 좌표, mm). 플립은 무시하고 회전·위치만 역적용한다(핸들 판정용). */
const componentToUnrotated = (node: BoxNode, point: PointMm): { x: number; y: number } => {
  const inverse = invert(nodeMatrix(withoutFlip(node)));
  return inverse === null ? { x: 0, y: 0 } : apply(inverse, { x: point.xMm, y: point.yMm });
};

/**
 * 이미지는 모서리 4개 + 회전(위 가운데에서 화면 24px 위), 텍스트는 좌우 변 + 회전(스펙 §6-3: 텍스트는 폭만 바꾸고 높이는 레이아웃).
 * 회전·위치를 반영한 컴포넌트 mm 다.
 */
export const handlePoints = (node: BoxNode, scale: HandleScale): readonly HandlePoint[] => {
  const unflipped = withoutFlip(node);
  const at = (kind: HandleKind, x: number, y: number): HandlePoint => ({
    kind,
    ...localToComponent(unflipped, x, y),
  });
  // 노드 로컬 위쪽(회전 반영)이 화면에서 1mm 당 몇 px 인지 — 축척이 축마다 달라도 화면에서 24px 위에 둔다.
  const axes = axesOf(scale);
  const rad = (node.rotationDeg * Math.PI) / 180;
  const upPxPerMm = Math.max(0.0001, Math.hypot(Math.sin(rad) * axes.x, Math.cos(rad) * axes.y));
  const rotate = at('rotate', node.width / 2, -ROTATE_HANDLE_OFFSET_PX / upPxPerMm);
  if (node.type === 'text')
    return [at('side-l', 0, node.height / 2), at('side-r', node.width, node.height / 2), rotate];
  return [
    at('corner-tl', 0, 0),
    at('corner-tr', node.width, 0),
    at('corner-br', node.width, node.height),
    at('corner-bl', 0, node.height),
    rotate,
  ];
};

export const hitHandle = (node: BoxNode, point: PointMm, scale: HandleScale): HandleKind | null => {
  const axes = axesOf(scale);
  for (const handle of handlePoints(node, scale)) {
    const dxPx = (handle.xMm - point.xMm) * axes.x;
    const dyPx = (handle.yMm - point.yMm) * axes.y;
    if (Math.hypot(dxPx, dyPx) <= HANDLE_HIT_TOLERANCE_PX) return handle.kind;
  }
  const local = componentToUnrotated(node, point);
  return local.x >= 0 && local.x <= node.width && local.y >= 0 && local.y <= node.height
    ? 'move'
    : null;
};

export const dragMove = <T extends BoxNode>(original: T, start: PointMm, current: PointMm): T => ({
  ...original,
  x: original.x + (current.xMm - start.xMm),
  y: original.y + (current.yMm - start.yMm),
});

export const nudge = <T extends BoxNode>(node: T, dxMm: number, dyMm: number): T => ({
  ...node,
  x: node.x + dxMm,
  y: node.y + dyMm,
});

/** 모서리를 끌면 중심을 고정하고 비율을 지킨다. 축척 = 현재점 중심 거리 ÷ 원래 모서리 중심 거리. */
export const dragResize = <T extends BoxNode>(
  original: T,
  corner: HandleKind,
  current: PointMm,
): T => {
  const c = center(original);
  const local = componentToUnrotated(original, current);
  const cornerLocal = {
    x: corner === 'corner-tl' || corner === 'corner-bl' ? 0 : original.width,
    y: corner === 'corner-tl' || corner === 'corner-tr' ? 0 : original.height,
  };
  const halfW = original.width / 2;
  const halfH = original.height / 2;
  const baseDistance = Math.hypot(cornerLocal.x - halfW, cornerLocal.y - halfH);
  const factor = baseDistance > 0 ? Math.hypot(local.x - halfW, local.y - halfH) / baseDistance : 1;
  const width = Math.max(MIN_NODE_MM, original.width * factor);
  const height = Math.max(MIN_NODE_MM, original.height * factor);
  return { ...original, width, height, x: c.xMm - width / 2, y: c.yMm - height / 2 };
};

/** (-180, 180] 로 접는다(정확히 180° 는 180 으로 둔다). */
export const normalizeDeg = (deg: number): number => {
  const wrapped = ((deg % 360) + 360) % 360;
  return wrapped > 180 ? wrapped - 360 : wrapped;
};

/** 회전 핸들이 위 가운데이므로 각도 = atan2(현재 − 중심) + 90°. snap 이면 15° 단위. */
export const dragRotate = <T extends BoxNode>(original: T, current: PointMm, snap: boolean): T => {
  const c = center(original);
  const raw = (Math.atan2(current.yMm - c.yMm, current.xMm - c.xMm) * 180) / Math.PI + 90;
  const deg = snap ? Math.round(raw / 15) * 15 : raw;
  return { ...original, rotationDeg: normalizeDeg(deg) };
};

/** 좌우 변을 끌어 줄바꿈 폭을 바꾼다. 반대쪽 변을 고정한다. 높이는 호출자가 textHeightMm 로 다시 계산한다. */
export const dragTextWidth = (
  original: TextNode,
  side: 'side-l' | 'side-r',
  current: PointMm,
): TextNode => {
  const local = componentToUnrotated(original, current);
  const width = Math.max(MIN_NODE_MM, side === 'side-r' ? local.x : original.width - local.x);
  // 고정할 변의 중점이 제자리에 있도록 새 중심을 회전축 방향으로 옮긴다(좌변 고정이면 +, 우변 고정이면 -).
  const shift = side === 'side-r' ? width - original.width : original.width - width;
  const rad = (original.rotationDeg * Math.PI) / 180;
  const c = center(original);
  const newCenterX = c.xMm + (shift / 2) * Math.cos(rad);
  const newCenterY = c.yMm + (shift / 2) * Math.sin(rad);
  return {
    ...original,
    width,
    x: newCenterX - width / 2,
    y: newCenterY - original.height / 2,
  };
};
